import React from 'react';
import get from 'lodash.get';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';

import { Observable, of, concat, merge, race, EMPTY, from } from 'rxjs';
import { mergeMap, catchError, filter, take, switchMap } from 'rxjs/operators';

import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import * as Actions from './s2p-purchase-and-print.actions';
import { emptyArray } from 'ui-components/default-props/empty';
import { getUserFriendlyMessagesForErrorResponse } from '../../errors/user-friendly-error-message-service';
import { SCAN_TO_PRINT_PURCHASE_AND_PRINT } from '../../job-pipelines/scan-to-print/purchase-and-print-pipeline';
import { REPRINT_LABEL_IN_SCAN_TO_PRINT } from '../../job-pipelines/scan-to-print/reprint-label';
import {
  shouldShowAmazonTermsError,
  showAmazonTermsErrorNotification,
} from '../../pages/amazon-buy-shipping-terms/error-notification-helper';
import { launchUpsLabelTermsModal } from '../../services/ups-label-terms-service';

import { makeBulkCreateLabelObservable } from '../bulk/bulk.label.observables';
import { startBulkRatingGetMessages } from '../csw/bulk-rate.observables';
import { ScanToPrintErrorService } from '../../scan/services/scan-to-print-error.service';
import { LogService } from '../../services/log-service';
import { shipstationConnect } from '../../services/shipstation-connect';
import * as BulkApi from '../../store/bulk/bulk.api';
import { shouldShowCostReviewForUpsTerms } from '../../store/cost-review/cost-review.helpers';
import { checkUpsLabelTermsRequired } from '../../store/seller/seller.api';
import * as CSWActions from '../../store/csw/csw.actions';
import * as CSWApi from '../../store/csw/csw.api';
import { getErrorMessagesFromSaveConfigAndRateResponse } from '../../store/csw/csw.error-helpers';
import * as Builders from '../../store/csw/csw.request-builders';
import Global from '../../store/global-selectors';
import * as InventoryActions from '../../store/inventory/inventory.actions';
import * as JobPipelineActions from '../../store/job-pipeline/job-pipeline.actions';
import * as ConnectActions from '../../store/shipstation-connect/ss-connect.actions';
import { Workflows } from '../../tracking/tracking-constants';
import { trackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { DocumentTypeEpicHelpers } from '../printing/document-type-epic-helpers';

const messages = defineMessages({
  processing: {
    description: 'scanToPrint.processing',
    defaultMessage: 'Processing',
    id: 'oWPHpK',
  },
  printing: {
    description: 'scanToPrint.printing',
    defaultMessage: 'Printing',
    id: 'BP9aoZ',
  },
  errorMessage: {
    description: 'scanToPrint.catchAllErrorMessage',
    defaultMessage: 'An unforeseen error has transpired. Please attempt the action again.',
    id: 'byW91x',
  },
});

export const letsBuyAndPrintSomeLabels = (action$) => {
  return action$.pipe(
    ofType(Actions.START_PURCHASE_AND_PRINT_JOB),
    mergeMap((action) => {
      const notificationId = NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        content: <FormattedMessage {...messages.processing} />,
        dontHide: true,
      });
      const { fulfillmentPlanId } = action;
      return merge(
        of(JobPipelineActions.startJobPipeline(SCAN_TO_PRINT_PURCHASE_AND_PRINT, { fulfillmentPlanId })),
        action$.pipe(
          ofType(JobPipelineActions.EXIT_JOB_PIPELINE, Actions.PRINT_SCAN_TO_PRINT_LABEL_REQUEST),
          take(1),
          switchMap(() => {
            NotificationService.removeNotification(notificationId);
            return EMPTY;
          }),
        ),
      );
    }),
  );
};

export const printExistingLabel = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_SCAN_TO_PRINT_LABEL),
    mergeMap(() => {
      const fulfillmentPlanId = Global.selectScanToPrintFulfillmentPlanId(getState());
      return of(
        JobPipelineActions.startJobPipeline(REPRINT_LABEL_IN_SCAN_TO_PRINT, {
          fulfillmentPlanId,
        }),
      );
    }),
  );
};

export const createLabel = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.FINALIZE_SCAN_TO_PRINT_FULFILLMENTPLAN),
    mergeMap(({ fulfillmentPlanId, jobId }) => {
      const state = getState();

      const errorResponse$ = (errors) => {
        return concat(of(JobPipelineActions.updateJob(jobId, { errors })), of(JobPipelineActions.exitJobPipeline(jobId)));
      };

      const payload = Builders.buildSaveShippingConfigurationAndRateRequest(state, {
        fulfillmentPlanId,
      });

      return CSWApi.updateFulfillmentPlanConfig(fulfillmentPlanId, payload, { shouldLock: true }).pipe(
        mergeMap((saveConfigRes) => {
          const errorMessages = getErrorMessagesFromSaveConfigAndRateResponse(saveConfigRes);
          if (errorMessages.length) {
            return errorResponse$(errorMessages);
          }

          return startBulkRatingGetMessages({
            apiCallback: () => from(BulkApi.bulkRateFulfillmentPlans({ fulfillmentPlanIds: [fulfillmentPlanId], forceRefreshRate: true })),
            pusherTimeoutMs: 180000,
            onError: () => EMPTY,
            logContext: { fulfillmentPlanId, operation: 's2p-create-label-rating' },
          }).pipe(
            mergeMap(({ findMessage$ }) =>
              findMessage$.pipe(
                mergeMap(({ pusherData }) => {
                  const orderData = pusherData?.Orders?.find((order) => order.OrderID?.toString() === fulfillmentPlanId.toString());
                  const rateError = orderData?.RateError;

                  if (rateError) {
                    return errorResponse$([rateError]);
                  }

                  const shouldShowAmazonError = shouldShowAmazonTermsError(errorMessages);
                  if (shouldShowAmazonError) {
                    showAmazonTermsErrorNotification();
                  }

                  return of(JobPipelineActions.goForwardOnJob(jobId));
                }),
                catchError(() => of(JobPipelineActions.goForwardOnJob(jobId))),
              ),
            ),
            catchError(() => of(JobPipelineActions.goForwardOnJob(jobId))),
          );
        }),
        catchError((errorResponse) => {
          const errors = get(errorResponse, 'response.data.Errors', emptyArray).map((e) => e.Message);
          return errorResponse$(errors);
        }),
      );
    }),
  );
};

const getFulfillments = (jobId, fulfillmentPlanId) => {
  return BulkApi.fulfillmentPlanToFulfillment([fulfillmentPlanId]).pipe(
    mergeMap((data = emptyArray) => {
      const fetchedFulfillmentIds = data.map((row) => get(row, 'fulfillmentId'));
      return concat(
        of(
          JobPipelineActions.updateJob(jobId, {
            fulfillments: fetchedFulfillmentIds,
          }),
        ),
        of(JobPipelineActions.goForwardOnJob(jobId)),
      );
    }),
    catchError((errorResponse) => {
      const errors = get(errorResponse, 'response.data.Errors', emptyArray).map((e) => e.Message);
      return concat(of(JobPipelineActions.updateJob(jobId, { errors })), of(JobPipelineActions.exitJobPipeline(jobId)));
    }),
  );
};

export const onLabelSuccessfullyCreated = (batchId, jobId, getState) => {
  const state = getState();
  if (jobId) {
    const jobData = Global.selectJobPipelineJobDataByJobId(state, { jobId });
    const scanToPrintBatchId = get(jobData, 'labelBatchIds.0');
    if (scanToPrintBatchId === batchId) {
      return getFulfillments(jobId, jobData.fulfillmentPlanId);
    }
  }
  return EMPTY;
};

/**
 * Creates an Observable that shows the UPS terms modal and waits for user response
 * @param {string} jobId - The job pipeline ID
 * @returns {Observable} - Emits { proceed: true } if accepted, or { proceed: false, exitAction } if declined
 */
const showUpsTermsModal$ = (jobId) => {
  return new Observable((observer) => {
    const modalId = launchUpsLabelTermsModal(
      () => {
        observer.next(true);
        observer.complete();
      },
      () => {
        observer.next(false);
        observer.complete();
      },
    );
    return () => ModalService.closeModalById(modalId, true);
  }).pipe(
    switchMap((accepted) => {
      if (accepted) {
        return of({ proceed: true });
      }
      return of({ proceed: false, exitAction: JobPipelineActions.exitJobPipeline(jobId) });
    }),
  );
};

/**
 * Checks if UPS terms are required, calling the API to handle grandfathering.
 * Returns an Observable that emits { needsTerms: boolean }
 */
const checkUpsTermsRequired$ = ({ fulfillmentPlanId, getState }) => {
  const mightNeedUpsTerms = shouldShowCostReviewForUpsTerms({ fulfillmentPlanId, getState });

  if (!mightNeedUpsTerms) {
    return of({ needsTerms: false });
  }

  // Call API to check if user is grandfathered (has previous UPS shipments)
  return from(checkUpsLabelTermsRequired()).pipe(
    switchMap((response) => {
      // If termsRequired is false, user is grandfathered - no modal needed
      // The API also updates the seller setting on the backend
      return of({ needsTerms: response.termsRequired });
    }),
    catchError(() => {
      // On error, assume terms are required to be safe
      return of({ needsTerms: true });
    }),
  );
};

export const useBulkToCreateLabelForScanToPrint = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.CREATE_LABEL_FOR_SCAN_TO_PRINT),
    mergeMap(({ fulfillmentPlanId, jobId }) => {
      const createLabel$ = () => {
        const state = getState();
        const labelObject = Builders.buildCreateLabelRequest(state, {
          fulfillmentPlanId,
        });
        const shipDate = labelObject?.shipDate ? new Date(labelObject?.shipDate) : new Date();
        const shipDateAsUTCMidnight = new Date(Date.UTC(shipDate.getFullYear(), shipDate.getMonth(), shipDate.getDate()));
        const metadata = {
          autoPrint: false,
          doNotPrint: true,
          labelObject,
          fromSingleLabel: true,
          scanToPrintJobId: jobId,
        };

        return makeBulkCreateLabelObservable({
          fulfillmentPlanIds: [fulfillmentPlanId],
          shipDate: shipDateAsUTCMidnight,
          action$,
          logContext: metadata,
          getState,
        }).pipe(
          switchMap(({ apiSuccess, labelBatchIds, finalMessage, errorMessage, hasCostReviewError }) => {
            if (apiSuccess) {
              return of(
                JobPipelineActions.updateJob(jobId, {
                  labelBatchIds,
                  metadata,
                }),
              );
            }

            if (finalMessage) {
              const scanToPrintJobId = Global.selectScanToPrintJobId(getState());
              if (scanToPrintJobId) {
                trackWorkflow(Workflows.SCAN_TO, WorkflowSteps.COMPLETE, {
                  jobId: scanToPrintJobId,
                  fulfillmentPlanId,
                  labelBatchIds,
                });

                return merge(
                  of(Actions.labelSuccessfullyCreatedForScanToPrint()),
                  onLabelSuccessfullyCreated(labelBatchIds[0], scanToPrintJobId, getState),
                );
              }
              return EMPTY;
            }

            if (errorMessage || hasCostReviewError) {
              const scanToPrintJobId = Global.selectScanToPrintJobId(getState());
              if (scanToPrintJobId) {
                const errors = [ScanToPrintErrorService.getUnknownErrorMessage()];
                return concat(
                  of(JobPipelineActions.updateJob(scanToPrintJobId, { errors })),
                  of(JobPipelineActions.exitJobPipeline(scanToPrintJobId)),
                );
              }
              return EMPTY;
            }

            return EMPTY;
          }),
          catchError((errorResponse) => {
            const resData = errorResponse?.response?.data;
            if (!resData) {
              // this isn't just a bad response, so don't swallow it
              LogService.logError(new Error('S2P label purchase error', { cause: errorResponse }));
              return EMPTY;
            }
            const { errors, stringErrors } = getUserFriendlyMessagesForErrorResponse(resData);
            const mappedErrors = errors.length
              ? errors.map((err) => {
                  if (err.apiError.Message.includes('Exception')) {
                    return messages.errorMessage;
                  }
                  return { ...err.messageDescriptor, values: err.values };
                })
              : stringErrors;
            return concat(of(JobPipelineActions.updateJob(jobId, { errors: mappedErrors })), of(JobPipelineActions.exitJobPipeline(jobId)));
          }),
        );
      };

      // Check if UPS terms are required (includes API call for grandfathering)
      return checkUpsTermsRequired$({ fulfillmentPlanId, getState }).pipe(
        switchMap(({ needsTerms }) => {
          if (needsTerms) {
            return showUpsTermsModal$(jobId).pipe(
              switchMap((result) => {
                if (result?.proceed) {
                  return createLabel$();
                }
                // Modal was declined, dispatch the exit action
                return of(result.exitAction);
              }),
            );
          }
          return createLabel$();
        }),
      );
    }),
  );
};

export const printDocumentRequest = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_SCAN_TO_PRINT_LABEL_REQUEST),
    mergeMap((action) => {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        content: <FormattedMessage {...messages.printing} />,
        duration: 3000,
      });
      const { printJob, jobId } = action;
      shipstationConnect.printDocument(printJob);
      return merge(
        of(JobPipelineActions.exitJobPipeline(jobId)),
        race(
          action$.pipe(
            ofType(ConnectActions.PRINT_DOCUMENT_SUCCESS),
            filter((act) => act.request === printJob.requestId),
            take(1),
            mergeMap(() =>
              DocumentTypeEpicHelpers.find((helper) => helper.id === printJob.documentType).getPrintedSuccessObservable(printJob),
            ),
          ),
          action$.pipe(
            ofType(ConnectActions.PRINT_DOCUMENT_FAILURE),
            filter((act) => act.requestId === printJob.requestId),
            take(1),
            mergeMap(() => {
              return EMPTY;
            }),
          ),
        ),
      );
    }),
  );
};

const defaultErrorHandling$ = (fulfillmentPlanId, errors, hasCostReviewError) =>
  concat(
    of(CSWActions.createLabelFailure(fulfillmentPlanId, errors, undefined, !hasCostReviewError)),
    of(InventoryActions.deallocateFulfillmentPlansSilently([fulfillmentPlanId])),
  );

export const handleErrorMessages = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.GENERIC_ERROR_HANDLING_FOR_SCAN_TO_PRINT),
    mergeMap(({ errors, batchId }) => {
      const state = getState();
      const fulfillmentPlanId = Global.selectScanToPrintFulfillmentPlanId(state);

      const costReviewErrors = errors.find((error) => ScanToPrintErrorService.isUnknownErrorMessage(error));
      if (costReviewErrors) {
        const stringyBatchId = batchId?.toString() || '';
        return BulkApi.getBulkCreateLabelErrors([stringyBatchId]).pipe(
          mergeMap((resp) => {
            const filteredErrors = resp.find((error) => error.labelBatchid === stringyBatchId);
            const batchItems = get(filteredErrors, 'items', emptyArray);
            const errorItems = batchItems.filter((error) => error.status === 'Error');

            // this is gross but the messages come with partial stack traces
            const errorMessages = errorItems.map((x) => x.errorMessage.split('Exception')[0]);
            return defaultErrorHandling$(fulfillmentPlanId, errorMessages, true);
          }),
        );
      }

      return defaultErrorHandling$(fulfillmentPlanId, errors, false);
    }),
  );
};

// used for reprint pipeline
export const getFulfillmentsFromFulfillmentPlans = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_FULFILLMENTS_FOR_FULFILLMENTPLAN),
    mergeMap((action) => {
      const { fulfillmentPlanId, jobId } = action;
      return getFulfillments(jobId, fulfillmentPlanId);
    }),
  );
};
